<script setup>
import { useTemplateRef, onBeforeUnmount, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { vOnClickOutside } from '@vueuse/components';

import Button from 'next/button/Button.vue';
import ConditionRow from 'dashboard/components-next/filter/ConditionRow.vue';
import FilterSelect from 'dashboard/components-next/filter/inputs/FilterSelect.vue';
import { useKanbanTaskFilterContext } from './provider';
import { DEFAULT_KANBAN_FILTER } from './helper';

const props = defineProps({
  board: { type: Object, default: () => ({}) },
  savedViews: { type: Array, default: () => [] },
  activeViewId: { type: Number, default: null },
});

const emit = defineEmits([
  'applyFilter',
  'selectView',
  'saveView',
  'deleteView',
  'close',
]);

const filters = defineModel({ type: Array, default: () => [] });

const { t } = useI18n();
const { filterTypes } = useKanbanTaskFilterContext(computed(() => props.board));

const conditionsRef = useTemplateRef('conditionsRef');

const resetFilter = () => {
  filters.value = [{ ...DEFAULT_KANBAN_FILTER }];
};

const removeFilter = index => {
  if (filters.value.length === 1) {
    resetFilter();
  } else {
    filters.value.splice(index, 1);
  }
};

const addFilter = () => {
  filters.value.push({ ...DEFAULT_KANBAN_FILTER });
};

const isValid = () =>
  Boolean(conditionsRef.value?.every(condition => condition.validate()));

const submit = () => {
  if (!isValid()) return;

  emit('applyFilter', filters.value);
};

// Saving hands over the edited rows, not whatever was last applied: the button
// shows as soon as a row has a value, so the two can differ.
const saveView = () => {
  if (!isValid()) return;

  emit('saveView', filters.value);
};

// values is an array for multiSelect, a bare {id, name} for the single selects
// and a string for plain text, so .length alone reports a picked contact or
// agent as empty and hides the save button.
const hasValue = values => {
  if (Array.isArray(values)) return values.length > 0;
  if (values && typeof values === 'object')
    return Object.keys(values).length > 0;
  return Boolean(values);
};

const hasConditions = computed(() =>
  filters.value.some(filter => hasValue(filter.values))
);

const viewOptions = computed(() => [
  { value: 'all', label: t('KANBAN.FILTERS.SAVED_VIEWS.ALL') },
  ...props.savedViews.map(view => ({
    value: String(view.id),
    label: view.name,
  })),
]);

onBeforeUnmount(() => emit('close'));

const outsideClickHandler = [
  () => emit('close'),
  { ignore: ['#toggleKanbanFilterButton'] },
];
</script>

<template>
  <div
    v-on-click-outside="outsideClickHandler"
    class="z-40 max-w-3xl lg:w-[750px] overflow-visible w-full border border-n-weak bg-n-alpha-3 backdrop-blur-[100px] shadow-lg rounded-xl p-6 grid gap-6"
  >
    <div class="flex items-center justify-between gap-4">
      <h3 class="text-base font-medium leading-6 text-n-slate-12">
        {{ t('KANBAN.FILTERS.ADVANCED.TITLE') }}
      </h3>
      <div v-if="savedViews.length" class="flex items-center gap-1">
        <FilterSelect
          :model-value="activeViewId ? String(activeViewId) : 'all'"
          :options="viewOptions"
          variant="faded"
          hide-icon
          class="text-sm"
          @update:model-value="emit('selectView', $event)"
        />
        <Button
          v-if="activeViewId"
          v-tooltip="t('KANBAN.FILTERS.SAVED_VIEWS.DELETE_TOOLTIP')"
          icon="i-lucide-trash"
          variant="ghost"
          color="slate"
          size="xs"
          @click="emit('deleteView')"
        />
      </div>
    </div>
    <ul class="grid gap-4 list-none">
      <template v-for="(filter, index) in filters" :key="`filter-${index}`">
        <ConditionRow
          v-if="index === 0"
          ref="conditionsRef"
          :key="`filter-${filter.attributeKey}-0`"
          v-model:attribute-key="filter.attributeKey"
          v-model:filter-operator="filter.filterOperator"
          v-model:values="filter.values"
          :filter-types="filterTypes"
          :show-query-operator="false"
          @remove="removeFilter(index)"
        />
        <ConditionRow
          v-else
          ref="conditionsRef"
          :key="`filter-${filter.attributeKey}-${index}`"
          v-model:attribute-key="filter.attributeKey"
          v-model:filter-operator="filter.filterOperator"
          v-model:query-operator="filters[index - 1].queryOperator"
          v-model:values="filter.values"
          show-query-operator
          :filter-types="filterTypes"
          @remove="removeFilter(index)"
        />
      </template>
    </ul>
    <div class="flex gap-2 justify-between">
      <Button sm ghost blue @click="addFilter">
        {{ t('FILTER.ADD_NEW_FILTER') }}
      </Button>
      <div class="flex gap-2">
        <Button
          v-if="hasConditions && !activeViewId"
          sm
          ghost
          slate
          icon="i-lucide-save"
          @click="saveView"
        >
          {{ t('KANBAN.FILTERS.SAVED_VIEWS.SAVE_TITLE') }}
        </Button>
        <Button sm faded slate @click="resetFilter">
          {{ t('FILTER.CLEAR_BUTTON_LABEL') }}
        </Button>
        <Button sm solid blue @click="submit">
          {{ t('FILTER.SUBMIT_BUTTON_LABEL') }}
        </Button>
      </div>
    </div>
  </div>
</template>
